import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';

/**
 * Combobox — text input with a live dropdown of suggestions.
 *
 * Uncontrolled "create or pick": users can type anything; the dropdown
 * offers matching options from a static list the parent provides. Picking
 * from the menu fills the input; continuing to type adjusts the match
 * list but never forces a selection — good for fields like counterparty
 * and short name where a brand-new value is sometimes the right answer.
 *
 * Props:
 *   value        string — current text value (controlled)
 *   onChange     fn(string) — called as the user types OR selects
 *   onSelect     fn(option) — optional; fired when a menu item is picked.
 *                Receives the full option object (for rich options) or the
 *                string value (for string[]). Not called for raw typing.
 *   options      Array<string | { value: string, label?: string, hint?: string, meta?: string }>
 *                If objects, `label` is what's shown, `value` is what's
 *                written to the input, `hint` is a small sub-line under
 *                the label, `meta` is a right-aligned tag (e.g. "3 deals").
 *   placeholder  string
 *   inputProps   object spread onto the input (type, maxLength, name…)
 *   maxVisible   number — cap on how many options render. Default 8.
 *   disabled     boolean
 *   emptyHint    string — shown as a muted line at the top of the menu
 *                when the user has typed something and no option matches.
 *                Useful for "Using '{typed}' as a new entry."
 */
export default function Combobox({
  value,
  onChange,
  onSelect,
  options = [],
  placeholder,
  inputProps = {},
  maxVisible = 8,
  disabled = false,
  emptyHint,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  // Viewport coords for the floating menu. Computed each time we open
  // (and on scroll/resize while open) so it escapes any ancestor with
  // overflow: clip — cards have that, which was cutting the menu off.
  const [menuPos, setMenuPos] = useState(null);

  // Normalize string[] → object[]. Rest of the component assumes objects.
  const normalized = useMemo(() => {
    return (options || []).map((o) =>
      typeof o === 'string' ? { value: o, label: o } : o
    );
  }, [options]);

  // Filter against the current input. Matches are ranked in three tiers
  // (exact prefix, word-boundary contains, any-substring) so the most
  // obvious match floats to the top. Filtering is case-insensitive and
  // ignores leading/trailing whitespace on the query.
  const filtered = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (!q) return normalized.slice(0, maxVisible);
    const starts = [];
    const wordBoundary = [];
    const contains = [];
    for (const o of normalized) {
      const hay = (o.label || o.value || '').toLowerCase();
      if (!hay) continue;
      if (hay === q) starts.unshift(o);
      else if (hay.startsWith(q)) starts.push(o);
      else if (hay.includes(' ' + q)) wordBoundary.push(o);
      else if (hay.includes(q)) contains.push(o);
    }
    return [...starts, ...wordBoundary, ...contains].slice(0, maxVisible);
  }, [value, normalized, maxVisible]);

  // Compute menu position relative to the viewport so position:fixed
  // bypasses ancestor overflow:clip. Flips up if there isn't enough
  // room below the input.
  const updateMenuPos = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const gap = 4;
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    const maxHeight = 280;
    // If there's less than maxHeight below but more above, flip up.
    const openUp = below < maxHeight && above > below;
    setMenuPos({
      left: rect.left,
      top: openUp ? rect.top - gap : rect.bottom + gap,
      width: rect.width,
      maxHeight: Math.min(maxHeight, Math.max(120, openUp ? above - gap - 8 : below - gap - 8)),
      openUp,
    });
  }, []);

  // Close when user clicks outside. Uses mousedown so we fire before
  // the blur-triggered close loses a click-selection. Also reposition
  // the menu on scroll/resize so it stays anchored to the input.
  //
  // Note: the menu is rendered OUTSIDE containerRef (direct body child
  // via portal would be cleaner, but position:fixed works for our
  // ancestors because none create a containing block for fixed
  // positioning). We use a ref on the menu itself for the click check.
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    updateMenuPos();
    const onDown = (e) => {
      const inContainer = containerRef.current && containerRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inContainer && !inMenu) setOpen(false);
    };
    const onScrollOrResize = () => updateMenuPos();
    document.addEventListener('mousedown', onDown);
    // Capture-phase so we catch scrolls on any ancestor (not just window).
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updateMenuPos]);

  // Keep the highlight in range as the filtered list changes.
  useEffect(() => {
    setHighlight((h) => Math.min(h, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  const pick = useCallback(
    (opt) => {
      if (!opt) return;
      onChange(opt.value);
      if (onSelect) onSelect(opt);
      setOpen(false);
      // Return focus to the input so the user can keep typing / tab away.
      inputRef.current && inputRef.current.focus();
    },
    [onChange, onSelect]
  );

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      if (open && filtered[highlight]) {
        e.preventDefault();
        pick(filtered[highlight]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    }
  };

  const showMenu = open && (filtered.length > 0 || (value && emptyHint));

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
      className={className}
    >
      <input
        ref={inputRef}
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="input"
        style={{ width: '100%' }}
        {...inputProps}
      />

      {showMenu && menuPos && (
        <div
          ref={menuRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: menuPos.openUp ? 'auto' : menuPos.top,
            bottom: menuPos.openUp ? (window.innerHeight - menuPos.top) : 'auto',
            left: menuPos.left,
            width: menuPos.width,
            zIndex: 1000,
            background: 'var(--surface-base)',
            border: 'var(--stroke-xs) solid var(--border-primary)',
            borderRadius: 'var(--corner-radius-md, 8px)',
            boxShadow: 'var(--shadow-md, 0 6px 16px rgba(0,0,0,0.1))',
            maxHeight: menuPos.maxHeight,
            overflowY: 'auto',
            padding: 4,
          }}
        >
          {filtered.length === 0 && emptyHint && (
            <div
              className="muted"
              style={{ padding: '8px 10px', fontSize: 12 }}
            >
              {emptyHint}
            </div>
          )}
          {filtered.map((opt, i) => (
            <div
              key={`${opt.value}-${i}`}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 'var(--corner-radius-sm, 6px)',
                cursor: 'pointer',
                background: i === highlight ? 'var(--surface-hover)' : 'transparent',
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {opt.label || opt.value}
                </div>
                {opt.hint && (
                  <div
                    className="muted"
                    style={{
                      fontSize: 11,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {opt.hint}
                  </div>
                )}
              </div>
              {opt.meta && (
                <span
                  className="muted"
                  style={{ fontSize: 11, flexShrink: 0 }}
                >
                  {opt.meta}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
